import React, { useEffect, useState, useRef } from "react";
import { Box, Grid, Typography, TextField } from "@mui/material";
import "typeface-raleway";
import { useNavigate } from "react-router-dom";

const Page5 = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({
    fullName: '',
    companyName: '',
    email: '',
  });

  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only set to visible if not already visible and 30% is in view
          if (!isVisible && entry.intersectionRatio >= 0.3) {
            setIsVisible(true);
            // Disconnect after first trigger to prevent going back to hidden
            observer.disconnect();
          }
        });
      },
      {
        threshold: [0.3] // Trigger when 30% of the element is visible
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  const validateForm = () => {
    let formIsValid = true;
    const newErrors = {
      fullName: '',
      companyName: '',
      email: '',
    };

    if (!fullName.trim()) {
      newErrors.fullName = 'Please enter your full name.';
      formIsValid = false;
    }
    if (!companyName.trim()) {
      newErrors.companyName = 'Please enter your company name.';
      formIsValid = false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address.';
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleJoinClick = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch('https://backend.towerwatch.io/api/waiting_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: fullName,
          companyName: companyName,
          email: email,
        }),
      });

      if (response.ok) {
        navigate('/Page6', { 
          state: { 
            fullName, 
            companyName, 
            email 
          } 
        });
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to join the waiting list.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error connecting to the server.');
    }
  };
  const handlePrivacyPolicyClick = () => {
    navigate('/privacy-policy'); // Navigate to the Privacy Policy page
  };

  return (
    <Box 
      ref={elementRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        height: '100vh',
        backgroundColor: '#000',
        color: '#fff',
        padding: '0 10%'
      }}
    >
      <Grid 
        container 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.6s ease-out 0.6s"
        }}
      >
        <Typography 
          variant="h3"
          sx={{
            fontSize: "32px",
            fontFamily: "Raleway",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.6s",
            color: "#7A7A7A",
            marginBottom: "1rem", 
            fontWeight: 300 
          }}
        >
          Coming Soon
        </Typography>
        
        <Typography 
          variant="h1"
          sx={{
            fontSize: "48px",
            fontFamily: "Raleway",
            color: "#FFFFFF",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.6s",
            fontWeight: 400, 
            lineHeight: "1.2" 
          }}
        >
          Get <Box component="span" sx={{ color: "#00bfff" }}>Notified</Box>
          <br />
          When We Launch
        </Typography>
      </Grid>

      <Grid 
        container 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "all 0.6s ease-out 0.6s"
        }}
      >
        <form style={{ width: '100%', maxWidth: '400px' }}>
          {/* Full Name Field */}
          <Grid 
            item 
            sx={{
              position: "relative", 
              width: "100%",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-out 0.6s",
              mb: 4,
              mt: { xs: 0, md: 4 }
            }}
          >
            <TextField
              fullWidth
              variant="standard"
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              error={!!errors.fullName}
              helperText={errors.fullName}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'white'
                },
                '& .MuiInputLabel-standard': {
                  color: '#7A7A7A',
                  transition: 'color 0.3s ease'
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: '#7A7A7A'
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: '#B67652'
                }
              }}
            />
          </Grid>

          {/* Company Name Field */}
          <Grid 
            item 
            sx={{
              position: "relative", 
              width: "100%",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-out 0.6s",
              mb: 4
            }}
          >
            <TextField
              fullWidth
              variant="standard"
              label="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'white'
                },
                '& .MuiInputLabel-standard': {
                  color: '#7A7A7A',
                  transition: 'color 0.3s ease'
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: '#7A7A7A'
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: '#B67652'
                }
              }}
            />
          </Grid>

          {/* Email Address Field */}
          <Grid 
            item 
            sx={{
              position: "relative", 
              width: "100%",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-out 0.6s",
              mb: 4
            }}
          >
            <TextField
              fullWidth
              variant="standard"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'white'
                },
                '& .MuiInputLabel-standard': {
                  color: '#7A7A7A',
                  transition: 'color 0.3s ease'
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: '#7A7A7A'
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: '#B67652'
                }
              }}
            />
          </Grid>
          <button
            onClick={handleJoinClick}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #fff",
              borderRadius: "10px",
              padding: "0.75rem",
              color: "#fff",
              fontSize: "16px",
              fontFamily: "Raleway",
              cursor: "pointer",
              width: "247px",
              alignSelf: "center",
              height: "38px",
              textAlign: "center",
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              marginLeft: "82px",
              marginTop: "70px",
              transition: "all 0.3s ease-out 0.6s",
              "&:hover": {
                backgroundColor: "#00bfff",
                color: "#fff"
              }
            }}
          >
            Join The Waiting List
          </button>
          {/* Container for LinkedIn and Privacy Policy */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "20px", // Adjust this value to increase/decrease space between links
              marginTop: "30px",
            }}
          >
            <Typography
              component="a"
              href="https://www.linkedin.com/company/towerwatch/posts/?feedView=all"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#7A7A7A",
                fontSize: "16px",
                fontFamily: "Raleway",
                opacity: isVisible ? 1 : 0,
                textDecoration: "none",
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transition: "all 0.6s ease-out 0.8s",
                "&:hover": { color: "#B67652" , textDecoration: "underline",},
                cursor: "pointer",
              }}
            >
              LinkedIn
            </Typography>

            <Typography
              component="span"
              onClick={handlePrivacyPolicyClick}
              sx={{
                color: "#7A7A7A",
                fontSize: "16px",
                fontFamily: "Raleway",
                
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transition: "all 0.6s ease-out 0.8s",
                "&:hover": { color: "#B67652",
                  textDecoration: "underline", },
                cursor: "pointer",
              }}
            >
              Privacy Policy
            </Typography>
          </Box>
        </form>
      </Grid>
    </Box>
  );
};

export default Page5;
